/**
 * media_signal igniter anomaly detail panel (#184, migration 0072). A
 * LOW-CONFIDENCE "needs assessment" chatter spike — NOT scored (no Response
 * Level), shown with the wire-blue pending badge (the smoke_detected idiom).
 * The EVIDENCE section is the point: the underlying signals (outlet, headline,
 * link, time) so the operator assesses from evidence, not a black box.
 * Two operator actions:
 *   • Confirm (sky)    → logged real; STAYS in the queue until the cluster
 *                        dissipates (auto-resolve).
 *   • Dismiss (slate)  → logged noise with an optional reason; leaves the queue.
 */
import { useState } from 'react';
import {
  confirmMediaSignalAnomaly,
  dismissMediaSignalAnomaly,
  type MediaSignalAnomalyFeature,
} from './api';
import {
  MEDIA_SIGNAL_ANOMALY_COLOR,
  MEDIA_SIGNAL_PENDING_BADGE_LABEL,
  mediaSignalHazardLabel,
} from './mediaSignalAnomalyStyle';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { RequestLiveEyes } from './RequestLiveEyes';
import { mediaSignalToQueueItem } from './anomalyQueue';
import { AnomalyAckButton } from './AnomalyAckButton';
import { AnomalyAssessmentControl } from './AnomalyAssessmentControl';

function agoLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

export function MediaSignalAnomalyBody({
  anomaly,
  onResolved,
}: {
  anomaly: MediaSignalAnomalyFeature;
  /** Called after a confirm/dismiss so the parent refreshes the queue (and, on
   *  dismiss, clears the selection — a confirmed row stays selectable). */
  onResolved?: (outcome: 'confirmed' | 'dismissed') => void;
}): JSX.Element {
  const p = anomaly.properties;
  const [lon, lat] = anomaly.geometry.coordinates;
  const [busy, setBusy] = useState<'confirm' | 'dismiss' | null>(null);
  const [reason, setReason] = useState('');
  // Optimistic local status so the badge flips on confirm without a refetch.
  const [status, setStatus] = useState(p.status);

  const confirm = async (): Promise<void> => {
    setBusy('confirm');
    try {
      const sessionId = getSessionId();
      logAction('confirm_media_signal_anomaly', {
        anomaly_id: anomaly.id,
        hazard_key: p.hazard_key,
        signal_count: p.signal_count,
        source_count: p.source_count,
      });
      await confirmMediaSignalAnomaly(anomaly.id, sessionId);
      setStatus('confirmed');
      showToast('Spike confirmed — stays in the queue until the chatter fades.', {
        variant: 'success',
      });
      onResolved?.('confirmed');
    } catch {
      showToast('Could not confirm the media-signal spike.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      const sessionId = getSessionId();
      logAction('dismiss_media_signal_anomaly', {
        anomaly_id: anomaly.id,
        reason: reason || null,
      });
      await dismissMediaSignalAnomaly(anomaly.id, sessionId, reason || undefined);
      showToast('Spike dismissed — logged as noise. Left the queue.', { variant: 'success' });
      onResolved?.('dismissed');
    } catch {
      showToast('Could not dismiss the media-signal spike.', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const ev = p.evidence;
  return (
    <div className="px-4 py-4 space-y-4" data-testid="media-signal-anomaly-body">
      <div className="border-l-2 pl-3" style={{ borderColor: MEDIA_SIGNAL_ANOMALY_COLOR }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Media-signal spike</p>
        <h2 className="text-base font-semibold">{mediaSignalHazardLabel(p.hazard_key)}</h2>
        <span
          className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium"
          style={{
            backgroundColor: `${MEDIA_SIGNAL_ANOMALY_COLOR}22`,
            color: MEDIA_SIGNAL_ANOMALY_COLOR,
          }}
        >
          {status === 'confirmed' ? 'Operator-confirmed — watching' : MEDIA_SIGNAL_PENDING_BADGE_LABEL}
        </span>
        {/* #391 — inline ✓ Ack (triage demote), reusing the queue's unified-workstate
            path. Unscored chatter spike ⇒ always ackable while unacked; coexists with
            the Confirm/Dismiss actions below. */}
        <div className="mt-2">
          <AnomalyAckButton item={mediaSignalToQueueItem(anomaly)} />
        </div>
        {/* #331 — the reversible ASSESSMENT axis. Distinct from ✓ Ack above and from the
            "Confirm — real event" watch action below. Closes the unrecoverable-dismiss hole. */}
        <div className="mt-2">
          <AnomalyAssessmentControl
            source="media_signal"
            sourceId={anomaly.id}
            initialAssessment={p.assessment ?? 'unconfirmed'}
          />
        </div>
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Spike</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Distinct outlets</dt>
          <dd>{p.source_count}</dd>
          <dt className="text-slate-400">Distinct stories</dt>
          <dd>{p.signal_count}</dd>
          <dt className="text-slate-400">Area baseline</dt>
          <dd>{p.baseline_per_window.toFixed(2)} / window</dd>
          <dt className="text-slate-400">First detected</dt>
          <dd>{agoLabel(p.first_detected_at)}</dd>
          <dt className="text-slate-400">Location</dt>
          <dd>
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
        <p className="mt-1.5 text-[11px] text-slate-500">
          No official feed reports this hazard here yet (suppression probe within{' '}
          {Math.round(ev.suppression.probed_radius_m / 1000)} km came back clear) — that gap is why
          this surfaced. Never auto-scored; your call below.
        </p>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Evidence — {ev.signals.length} signals
        </h3>
        <ul className="space-y-1.5">
          {ev.signals.map((s) => (
            <li key={s.id} className="text-[12px] leading-snug">
              <div className="flex items-baseline gap-1.5">
                <span className="shrink-0 text-slate-400">{s.origin.replace(/^[a-z]+:/, '')}</span>
                <span className="shrink-0 text-slate-600">·</span>
                <span className="shrink-0 text-slate-500">{agoLabel(s.observed_at)}</span>
              </div>
              {s.url ? (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sky-300 hover:underline break-words"
                >
                  {s.title ?? s.url}
                </a>
              ) : (
                <span className="text-slate-200">{s.title ?? '(untitled)'}</span>
              )}
              {s.geo_name && <span className="ml-1 text-slate-500">— {s.geo_name}</span>}
            </li>
          ))}
        </ul>
      </section>

      <section className="text-[11px] text-slate-500">
        Gate: ≥{ev.thresholds.min_signals} stories from ≥{ev.thresholds.min_origins} outlets within{' '}
        {Math.round(ev.thresholds.radius_m / 1000)} km / {ev.thresholds.window_min} min, above 4× the
        area baseline.
      </section>

      {status !== 'confirmed' && (
        <section className="space-y-2">
          <button
            type="button"
            data-testid="media-signal-confirm"
            disabled={busy !== null}
            onClick={() => void confirm()}
            className="w-full py-1.5 rounded text-sm font-semibold bg-sky-600/80 hover:bg-sky-600 text-white disabled:opacity-50"
          >
            {busy === 'confirm' ? 'Confirming…' : 'Confirm — real event, keep watching'}
          </button>
        </section>
      )}
      <section className="space-y-2">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="dismiss reason (optional)"
          className="w-full text-xs px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 outline-none focus:border-sky-500"
        />
        <button
          type="button"
          data-testid="media-signal-dismiss"
          disabled={busy !== null}
          onClick={() => void dismiss()}
          className="w-full py-1.5 rounded text-sm font-semibold bg-slate-700/80 hover:bg-slate-700 text-slate-200 disabled:opacity-50"
        >
          {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss — noise'}
        </button>
      </section>

      {/* #348b — drone marketplace front door, anchored on this anomaly. */}
      <RequestLiveEyes ao={{ lat, lon }} aoLabel="this media-signal spike" />
    </div>
  );
}
